// Eskom PARCC — overlay surfaces shared by both themes.
//
// `Flyout` is one dismissible panel with two geometries: a centred modal card
// under Legacy (reusing the `.modal-backdrop` / `.modal-card` classes the app
// already ships) and the handoff's right-hand slide-in under Lumen. `SplitSurface`
// builds on it for the list+detail screens: Legacy keeps the two-column `.split`
// it has always had, Lumen promotes the list to full width and floats the detail
// in a 65% flyout.
//
// One component, two shapes — so the actions inside the detail (Edit, CES,
// OpenText, …) are literally the same JSX in both themes and cannot drift.
import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useVariant } from '../../lib/uiPrefs';

/** Close on Escape while `active`. Registered on the document so it fires
 *  wherever focus happens to be (the handoff specifies a global keydown). */
export function useEscape(active: boolean, onEscape: () => void): void {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onEscape();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, onEscape]);
}

export interface FlyoutProps {
  open: boolean;
  onClose: () => void;
  /** Panel heading. Also the dialog's accessible name. */
  title: ReactNode;
  subtitle?: ReactNode;
  /** Rendered opposite the title (a stat, a pill, a row of actions). */
  headerExtra?: ReactNode;
  /** `wide` = the 65% transaction flyout; `narrow` = the 560px drill panel. */
  width?: 'wide' | 'narrow';
  children: ReactNode;
  /** Accessible name when `title` is not a plain string. */
  ariaLabel?: string;
}

export function Flyout({
  open,
  onClose,
  title,
  subtitle,
  headerExtra,
  width = 'narrow',
  children,
  ariaLabel,
}: FlyoutProps): React.JSX.Element | null {
  const variant = useVariant();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);

  const close = useCallback(() => onClose(), [onClose]);
  useEscape(open, close);

  // Move focus into the panel on open and hand it back on close, so a keyboard
  // user is not stranded behind the scrim.
  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => {
      restoreTo.current?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  const name = ariaLabel ?? (typeof title === 'string' ? title : undefined);

  const head = (
    <div className="lm-flyout-head">
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button type="button" className="lm-closebtn" onClick={close} aria-label="Close">
            <X size={16} aria-hidden />
          </button>
          <h2>{title}</h2>
        </div>
        {subtitle !== undefined && (
          <div className="small" style={{ marginTop: 8 }}>
            {subtitle}
          </div>
        )}
      </div>
      {headerExtra}
    </div>
  );

  const body = (
    <>
      {head}
      {children}
    </>
  );

  if (variant === 'lumen') {
    return createPortal(
      <>
        <div className="lm-scrim" style={{ zIndex: 210 }} onClick={close} aria-hidden />
        <div
          ref={panelRef}
          className={`lm-flyout ${width}`}
          style={{ zIndex: 211 }}
          role="dialog"
          aria-modal="true"
          aria-label={name}
          tabIndex={-1}
        >
          {body}
        </div>
      </>,
      document.body,
    );
  }

  // Legacy: the centred modal card the app already uses everywhere else.
  return createPortal(
    <div className="modal-backdrop open" onClick={close}>
      <div
        ref={panelRef}
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={name}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: width === 'wide' ? 1080 : 620 }}
      >
        {body}
      </div>
    </div>,
    document.body,
  );
}

export interface SplitSurfaceProps {
  /** The register / list column. Full width under Lumen. */
  list: ReactNode;
  /** The detail panel's content. Rendered inline (Legacy) or in a flyout (Lumen). */
  detail: ReactNode;
  /** Has the reader explicitly opened a row? Lumen only shows the flyout then;
   *  Legacy always shows its second column, exactly as it does today. */
  open: boolean;
  onClose: () => void;
  /** Flyout heading (Lumen only — Legacy's detail carries its own header). */
  title?: ReactNode;
  subtitle?: ReactNode;
  headerExtra?: ReactNode;
  ariaLabel?: string;
}

export function SplitSurface({
  list,
  detail,
  open,
  onClose,
  title,
  subtitle,
  headerExtra,
  ariaLabel,
}: SplitSurfaceProps): React.JSX.Element {
  const variant = useVariant();

  if (variant === 'lumen') {
    return (
      <>
        {list}
        <Flyout
          open={open}
          onClose={onClose}
          title={title ?? 'Detail'}
          subtitle={subtitle}
          headerExtra={headerExtra}
          width="wide"
          ariaLabel={ariaLabel}
        >
          {detail}
        </Flyout>
      </>
    );
  }

  return (
    <div className="split">
      {list}
      {detail}
    </div>
  );
}
